import { useEffect } from "react"
import { useForm } from "@inertiajs/react"
import { settings_task_settings_path } from "@/lib/routes"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/Tabs"
import { NumberField } from "@/pages/Settings/components/PolicyFields"
import PeopleCircles from "@/components/PeopleCircles"
import ReviewerFields from "@/pages/Settings/components/ReviewerFields"
import SettingsSection from "@/pages/Settings/components/SettingsSection"
import type { Person } from "@/lib/person"
import type { ChannelPicSetting, ChannelTaskMarket } from "@/pages/Settings/TaskSettings"

// Filled once every channel has someone in charge. A market with no channels
// has nothing to fill here, so it passes. Days always hold a number and
// reviewers are optional, so neither counts.
function filled(m: ChannelTaskMarket) {
  return m.channels.every((c) => c.pic_user_ids.length > 0)
}

// One tab per market: the task due date (one number for the whole market)
// and the PICs per sale channel. Shared by the Products & Pricings and the
// Monthly Plan sections: `field` names the controller arrays this card saves
// (`${field}_markets` for due dates, `${field}` for channel rows). Its own
// form: Save sends only this section's rows.
export default function ChannelTaskCard({ id, title, field, markets, people, readOnly, onUnfilledChange }: {
  id: string
  title: string
  field: "products_pricing" | "monthly_plan"
  markets: ChannelTaskMarket[]
  people: Person[]
  readOnly: boolean
  // Reports whether any market is still unfilled, from the live form values.
  onUnfilledChange?: (id: string, unfilled: boolean) => void
}) {
  const { data, setData, patch, processing, transform } = useForm<{ markets: ChannelTaskMarket[] }>({ markets })
  const disabled = readOnly || processing

  const unfilled = data.markets.some((m) => !filled(m))
  useEffect(() => { onUnfilledChange?.(id, unfilled) }, [id, unfilled, onUnfilledChange])

  // The controller takes one due_in_days row per market and flat PIC rows per
  // channel; the market → channels nesting is display-only.
  transform((d) => ({
    [`${field}_markets`]: d.markets.map((m) => ({ org_node_id: m.org_node_id, due_in_days: m.due_in_days })),
    [field]: d.markets.flatMap((m) =>
      m.channels.map((c) => ({ org_node_id: c.org_node_id, pic_user_ids: c.pic_user_ids,
        reviewer_user_ids: c.reviewer_user_ids, skip_review: c.skip_review })),
    ),
  }))

  const updateMarket = (org_node_id: string, patchRow: Partial<ChannelTaskMarket>) => {
    setData("markets", data.markets.map((m) =>
      (m.org_node_id === org_node_id ? { ...m, ...patchRow } : m),
    ))
  }

  const updateChannel = (channel_org_node_id: string, patchRow: Partial<ChannelPicSetting>) => {
    setData("markets", data.markets.map((m) => ({
      ...m,
      channels: m.channels.map((c) => (c.org_node_id === channel_org_node_id ? { ...c, ...patchRow } : c)),
    })))
  }

  return (
    <SettingsSection
      id={id}
      title={title}
      canSave={!readOnly}
      processing={processing}
      onSave={() => patch(settings_task_settings_path(), { preserveScroll: true })}
    >
      {data.markets.length === 0 ? (
        <p className="text-sm text-muted-foreground">No markets yet — add country nodes in Org Structure first.</p>
      ) : (
        <Tabs defaultValue={data.markets[0].org_node_id} className="flex flex-col gap-5">
          <TabsList>
            {data.markets.map((market) => (
              <TabsTrigger key={market.org_node_id} value={market.org_node_id} dot={!filled(market)}>{market.name}</TabsTrigger>
            ))}
          </TabsList>
          {data.markets.map((market) => (
            <TabsContent key={market.org_node_id} value={market.org_node_id}>
              <div className="flex flex-col gap-3.5">
                <p className="flex items-center gap-2 text-sm text-foreground">
                  <span className="w-40 shrink-0">Task due</span>
                  <NumberField
                    value={market.due_in_days}
                    onChange={(n) => updateMarket(market.org_node_id, { due_in_days: n })}
                    min={0}
                    disabled={disabled}
                    label={`${market.name} due in days after task created`}
                  />{" "}
                  days after task created
                </p>

                {market.channels.length === 0 ? (
                  <p className="text-sm text-muted-foreground">
                    No channels yet — add channel nodes under {market.name} in Org Structure first.
                  </p>
                ) : (
                  market.channels.map((channel) => (
                    <div key={channel.org_node_id} className="flex flex-col gap-2 rounded-md border bg-card p-3">
                      <p className="text-sm text-foreground">{channel.name}</p>
                      <div className="flex items-start gap-2">
                        <span className="w-40 shrink-0 pt-1.5 text-sm text-foreground">People in Charge</span>
                        <PeopleCircles
                          title="People in Charge"
                          people={people}
                          selectedIds={channel.pic_user_ids}
                          onChange={(ids) => updateChannel(channel.org_node_id, { pic_user_ids: ids })}
                          editable={!readOnly}
                          disabled={disabled}
                        />
                      </div>
                      <ReviewerFields
                        skipReview={channel.skip_review}
                        reviewerIds={channel.reviewer_user_ids}
                        people={people}
                        readOnly={readOnly}
                        disabled={disabled}
                        onChange={(patchRow) => updateChannel(channel.org_node_id, patchRow)}
                      />
                    </div>
                  ))
                )}
              </div>
            </TabsContent>
          ))}
        </Tabs>
      )}
    </SettingsSection>
  )
}
